import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createAssessmentSchema } from "models/schemas";
import { GenericDraftWizard } from "../generic-draft-wizard";
import { createNoticeWizardConfig } from "./noticeWizardConfig";
import { INoticeDraftWizardProps } from "./definitions";
import { useLocalStorage } from "hooks/useLocalStorage";
import { GaiaObjectFormRef } from "../create-edit-gaiaobject-form/definitions";
import { useNotifications } from "features/notifications";
import { useCreateGaiaObjectMutation } from "../create-edit-gaiaobject-form/endpoints";
import { getGaiaObjectErrorMessage } from "../create-edit-gaiaobject-form/util";
import { useGetScanjobQuery } from "pages/draft-overview/endpoints";
import { useDownloadUrlQuery } from "components/documents-table/endpoints";
import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content";
import { useGaiaObjectCustomAttributes } from "hooks/useGaiaObjectCustomAttributes";
import { processGaiaObjectForSubmission } from "utils/gaiaobject-utils";
import { GaiaConstants } from "utils/gaia-constants";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { Spinner, Alert, Box } from "@amzn/awsui-components-react";
import { useCreateAssessmentMutation } from "pages/create-assessment/endpoints";
import { useRegisterDocumentMutation } from "components/create-audit-form/endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import {
  registerScanDocument,
  updateScanStatus,
  createAssessmentFromData,
  getNotificationConfig,
} from "utils/scan-job-utils";
import styles from "./style.module.scss";

const NoticeDraftWizard: React.FC<INoticeDraftWizardProps> = ({
  visible = true,
  onDismiss,
  initialData,
  category,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerWizardValidation, setTriggerWizardValidation] = useState(0);
  const [isFilePreviewVisible, setIsFilePreviewVisible] = useState(true);
  const [isAssessmentSimplified, setIsAssessmentSimplified] = useState(false);
  const formRef = useRef<GaiaObjectFormRef>(null);
  const { addNotification } = useNotifications();
  const [createGaiaObjectCall] = useCreateGaiaObjectMutation();
  const [createAssessment] = useCreateAssessmentMutation();
  const [registerDocumentCall] = useRegisterDocumentMutation();
  const [updateScanJobCall] = useUpdateScanJobMutation();
  const teamName = useAppSelector((state) => state.audits.teamName);
  const navigate = useNavigate();
  const { shortId } = useParams<{ shortId: string }>();

  const localStorageKey = `notice-draft-wizard-step1-${shortId}`;
  const assessmentStorageKey = `assessment-step-${shortId}`;
  const uiContent = useGaiaObjectUiContent(category);
  const { customAttributes, createSchema } =
    useGaiaObjectCustomAttributes(category);

  const [storedFormData, setStoredFormData] = useLocalStorage<any>(
    localStorageKey,
    null
  );

  const { control } = useForm({
    mode: "onChange",
    resolver: zodResolver(createAssessmentSchema),
  });

  const {
    data: scanJobData,
    error: scanJobError,
    isLoading: scanJobLoading,
  } = useGetScanjobQuery(
    { shortId: shortId as string },
    { refetchOnMountOrArgChange: true, skip: !shortId }
  );

  const {
    data: downloadUrlData,
    error: downloadUrlError,
    isLoading: downloadUrlLoading,
  } = useDownloadUrlQuery(
    { documentId: shortId as string, preview: true },
    {
      skip: !shortId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );

  useEffect(() => {
    return () => {
      if (shortId) {
        localStorage.removeItem(localStorageKey);
        localStorage.removeItem(assessmentStorageKey);
      }
    };
  }, [localStorageKey, assessmentStorageKey, shortId]);

  const processGaiaObjectData = useCallback(
    (data: any) => {
      return processGaiaObjectForSubmission(
        data,
        teamName,
        "notice",
        customAttributes,
        uiContent?.entityOptions
      );
    },
    [customAttributes, teamName, uiContent?.entityOptions]
  );

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);

    try {
      // Prepare and create notice
      const formDataToSubmit = {
        titleSuffix: storedFormData.titleSuffix,
        template: storedFormData.template,
        [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: {
          ...storedFormData[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
        },
      };

      const processedData = processGaiaObjectData(formDataToSubmit);
      const noticeResponse = await createGaiaObjectCall(processedData).unwrap();

      // Execute secondary operations and collect warnings
      const warnings: string[] = [];

      const documentError = await registerScanDocument(
        shortId,
        scanJobData,
        noticeResponse.shortId,
        teamName,
        registerDocumentCall
      );
      if (documentError) warnings.push(documentError);

      const scanStatusError = await updateScanStatus(
        shortId,
        noticeResponse.shortId,
        updateScanJobCall
      );
      if (scanStatusError) warnings.push(scanStatusError);

      const assessmentResult = await createAssessmentFromData(
        noticeResponse.shortId,
        assessmentStorageKey,
        shortId,
        isAssessmentSimplified,
        createAssessment
      );

      // Show appropriate notification
      const notificationConfig = getNotificationConfig(
        processedData.title,
        assessmentResult,
        warnings
      );

      addNotification({
        ...notificationConfig,
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });

      // Clean up and navigate
      localStorage.removeItem(localStorageKey);
      localStorage.removeItem(assessmentStorageKey);
      navigate("/notices");
      onDismiss?.();
    } catch (error: any) {
      console.error("Error creating notice:", error);
      const errorMessage = getGaiaObjectErrorMessage(
        error,
        "notice",
        error?.message || "An unexpected error occurred"
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    storedFormData,
    processGaiaObjectData,
    scanJobData,
    teamName,
    localStorageKey,
    assessmentStorageKey,
    shortId,
    isAssessmentSimplified,
    registerDocumentCall,
    updateScanJobCall,
    createAssessment,
    createGaiaObjectCall,
    addNotification,
    navigate,
    onDismiss,
  ]);

  const handleCancel = useCallback(() => {
    localStorage.removeItem(localStorageKey);
    localStorage.removeItem(assessmentStorageKey);
    navigate("/drafts");
    onDismiss?.();
  }, [localStorageKey, assessmentStorageKey, navigate, onDismiss]);

  const stepAdvancementRef = useRef<((stepIndex: number) => void) | null>(null);

  const handleStepValidationSuccess = useCallback((data: any) => {
    if (stepAdvancementRef.current) {
      stepAdvancementRef.current(1);
    }
  }, []);

  const triggerFormValidation = useCallback(() => {
    setTriggerWizardValidation((prev) => prev + 1);
  }, []);

  const config = useMemo(
    () =>
      createNoticeWizardConfig(
        handleComplete,
        handleCancel,
        scanJobData,
        downloadUrlData,
        shortId,
        handleStepValidationSuccess,
        triggerWizardValidation,
        triggerFormValidation,
        formRef,
        isFilePreviewVisible,
        setIsFilePreviewVisible,
        isAssessmentSimplified,
        setIsAssessmentSimplified,
        control
      ),
    [
      handleComplete,
      handleCancel,
      scanJobData,
      downloadUrlData,
      shortId,
      handleStepValidationSuccess,
      triggerWizardValidation,
      triggerFormValidation,
      isFilePreviewVisible,
      isAssessmentSimplified,
      control,
    ]
  );

  config.setStepAdvancementCallback = (
    callback: (stepIndex: number) => void
  ) => {
    stepAdvancementRef.current = callback;
  };

  config.formRef = formRef;
  config.setStoredFormData = setStoredFormData;

  if (!visible) {
    return null;
  }

  const isLoading = scanJobLoading || downloadUrlLoading;
  if (isLoading) {
    return (
      <div
        className={styles.noticeDraftWizard}
        data-testid="notice-draft-wizard"
      >
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" padding={{ top: "m" }}>
            Loading data...
          </Box>
        </Box>
      </div>
    );
  }

  const hasError = scanJobError || downloadUrlError;
  if (hasError) {
    const errorMessage = scanJobError
      ? `Failed to load: ${(scanJobError as any)?.message || "Unknown error"}`
      : `Failed to preview: ${
          (downloadUrlError as any)?.message || "Unknown error"
        }`;

    return (
      <div
        className={styles.noticeDraftWizard}
        data-testid="notice-draft-wizard"
      >
        <Alert
          type="error"
          header="Failed to load data"
          action={
            <Box>
              <button onClick={handleCancel}>Return to Notices</button>
            </Box>
          }
        >
          {errorMessage}
        </Alert>
      </div>
    );
  }

  return (
    <div className={styles.noticeDraftWizard} data-testid="notice-draft-wizard">
      <GenericDraftWizard
        config={config}
        visible={visible}
        onDismiss={onDismiss}
        initialData={initialData}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default NoticeDraftWizard;
